import * as React from "react";
import * as Icons from "./Icons";
import ChatMessageBox from "./ChatMessageBox";
import UploadButton from "./UploadButton";
import ConfigContext from "../context/ConfigContext";
import { EngagementConfiguration, DataField } from "../types";
import isCorsSupported from "../helpers/isCorsSupported";

interface ConfigExtension {
  config: EngagementConfiguration;
}

interface ChatMessageFormProps {
  visitorId: string;
  engagementId: string;
  engagementApiEndpoint: string;
  maxFileSizeBytes: number;
  fileSizeErrorMessage: string;
  siteId: number;
  dataField: DataField;
  onSendMessage: (text: string) => void;
  onTyping: (message: string) => void;
  onUploadBegin: () => void;
  onUploadProgress: (percent: number) => void;
  onUploadComplete: () => void;
  onSubmitDataField: (value: string) => void;
}

interface ChatMessageFormState {
  text: string;
}

/**
 * Any changes to this component or its children should be accompanied by
 * a `yarn build` and testing the Engagement Design screen in agent console.
 * Keep this component as dumb as possible so it can be easily reused.
 */

class ChatMessageForm extends React.Component<
  ChatMessageFormProps & ConfigExtension,
  ChatMessageFormState
> {
  state = {
    text: ""
  };

  onChangeText = (text: string) => {
    this.props.onTyping(text);
    this.setState({ text });
  };

  sendText = () => {
    const text = this.state.text.trim();

    if (!text) {
      return;
    }

    if (this.props.dataField) {
      this.props.onSubmitDataField(text);
    } else {
      this.props.onSendMessage(text);
    }

    this.setState({ text: "" });
  };

  onClickSendButton = () => {
    this.sendText();
  };

  renderUploadButton() {
    if (!isCorsSupported()) {
      return null;
    }

    if (!this.props.config.UploadEnabled) {
      return null;
    }

    return (
      <UploadButton
        onBegin={this.props.onUploadBegin}
        onProgress={this.props.onUploadProgress}
        onComplete={this.props.onUploadComplete}
        visitorId={this.props.visitorId}
        engagementId={this.props.engagementId}
        engagementApiEndpoint={this.props.engagementApiEndpoint}
        maxFileSizeBytes={this.props.maxFileSizeBytes}
        fileSizeErrorMessage={this.props.fileSizeErrorMessage}
        siteId={this.props.siteId}
      />
    );
  }

  render() {
    return (
      <div className="velaro-message-form">
        <ChatMessageBox
          value={this.state.text}
          onChange={this.onChangeText}
          onEnter={() => this.sendText()}
          placeholder={this.props.config.ChatPlaceholder}
        />
        <div className="velaro-message-form-actions">
          {this.renderUploadButton()}
          <button
            className="velaro-message-send-button velaro-bg-primary"
            onClick={this.onClickSendButton}
            role="button"
            aria-label="Send Message"
          >
            <Icons.SendIcon color={this.props.config.PrimaryTextColor} />
          </button>
        </div>
      </div>
    );
  }
}

export default (props: ChatMessageFormProps) => (
  <ConfigContext.Consumer>
    {(config) => <ChatMessageForm config={config} {...props} />}
  </ConfigContext.Consumer>
);
